import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import "../forms/themeCheckbox.css";
import { addLogoAndFormHeader, applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import CheckTick from "../../assets/icons/Check-Tick.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";

const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const GREY_BAR = [217, 217, 217];
const PDF_WHITE = [255, 255, 255];

const APPOINTED_ROLE_OPTIONS = ["Senior Authorised Person", "Authorised Person"];
const LV_PERMIT_AUTHORITY_OPTIONS = ["includes", "does not include"];
const HE_OR_SHE_OPTIONS = ["He", "She"];

const DEFAULT_FORM_DATA = {
  site: "",
  projectNo: "",
  date: "",
  certification: { name: "", signature: "" },
  appointedRole: "",
  lvPermitAuthority: "",
  heOrShe: "",
  seniorAuthorisation: { authorisedBy: "", signature: "", date: "" },
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  return {
    site: src.site ?? hd.site ?? "",
    projectNo: src.projectNo ?? hd.projectNo ?? "",
    date: src.date ?? hd.date ?? "",
    certification: { ...DEFAULT_FORM_DATA.certification, ...(src.certification || {}) },
    appointedRole: normalizeAppointedRole(src.appointedRole ?? src.appointed_role),
    lvPermitAuthority: normalizeLvPermitAuthority(src.lvPermitAuthority ?? src.lv_permit_authority ?? src.lvPermit),
    heOrShe: normalizeHeOrShe(src.heOrShe ?? src.he_or_she),
    seniorAuthorisation: { ...DEFAULT_FORM_DATA.seniorAuthorisation, ...(src.seniorAuthorisation || {}) },
  };
}

function dateToLocalYMD(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeAppointedRole(val) {
  const s = String(val ?? "").trim();
  return APPOINTED_ROLE_OPTIONS.includes(s) ? s : "";
}

function normalizeLvPermitAuthority(val) {
  const s = String(val ?? "").trim();
  return LV_PERMIT_AUTHORITY_OPTIONS.includes(s) ? s : "";
}

function normalizeHeOrShe(val) {
  const s = String(val ?? "").trim();
  if (s === "He" || s.toLowerCase() === "he") return "He";
  if (s === "She" || s.toLowerCase() === "she") return "She";
  return "";
}

function getSf009AValidationError(data) {
  const src = data && typeof data === "object" ? data : {};
  const errors = [];

  const check = (field, value, allowed) => {
    if (value == null || String(value).trim() === "") return;
    const s = String(value).trim();
    if (!allowed.includes(s)) {
      errors.push(`${field} must be one of: ${allowed.map((v) => `"${v}"`).join(" | ")}`);
    }
  };

  check("appointedRole", src.appointedRole ?? src.appointed_role, APPOINTED_ROLE_OPTIONS);
  check("lvPermitAuthority", src.lvPermitAuthority ?? src.lv_permit_authority ?? src.lvPermit, LV_PERMIT_AUTHORITY_OPTIONS);
  const heOrSheRaw = src.heOrShe ?? src.he_or_she;
  if (heOrSheRaw != null && String(heOrSheRaw).trim() !== "" && !normalizeHeOrShe(heOrSheRaw)) {
    errors.push(`heOrShe must be one of: ${HE_OR_SHE_OPTIONS.map((v) => `"${v}"`).join(" | ")}`);
  }

  return errors.length ? errors.join(". ") : null;
}

function drawPdfTickInCell(doc, data, active) {
  if (!active || data.section !== "body") return;
  const iconSize = 3.4;
  const iconX = data.cell.x + data.cell.width / 2 - iconSize / 2;
  const iconY = data.cell.y + data.cell.height / 2 - iconSize / 2;
  try {
    doc.addImage(CheckTick, "PNG", iconX, iconY, iconSize, iconSize);
  } catch {}
}

function parseDateForPicker(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const slashOrDash = parseSiteRegisterDate(value);
  if (slashOrDash) return slashOrDash;
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME_LIGHT,
  color: THEME,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 12,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009HdrCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME_LIGHT,
  color: THEME,
};
const sf009TxtCell = {
  border: sf009TblBorder,
  padding: "10px 12px",
  fontSize: 12,
  background: "#fff",
  lineHeight: 1.55,
  verticalAlign: "top",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 12,
  padding: "4px 2px",
  background: "transparent",
  boxSizing: "border-box",
};

function Sf009ACertificateElectricalPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = (f) => {
    if (!f || typeof f !== "object") return {};
    if (f.formData && typeof f.formData === "object") return f.formData;
    const { formData, userId, createdBy, _id, __v, createdAt, updatedAt, ...rest } = f;
    return rest;
  };

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-a-certificate-electrical", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const cert = fd.certification || {};
    const fields = [fd.site, fd.projectNo, cert.name, f.name].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (section, key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section === "_flat") {
        next.formData[key] = value;
      } else if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      }
      const fd = next.formData;
      next.name = fd.certification?.name || fd.seniorAuthorisation?.authorisedBy || "";
      next.date = fd.date || fd.seniorAuthorisation?.date || "";
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergeRecordIntoDefaults(raw),
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    const validationError = getSf009AValidationError(editForm.formData);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const raw = { ...(editForm.formData || {}) };
      raw.appointedRole = normalizeAppointedRole(raw.appointedRole);
      raw.lvPermitAuthority = normalizeLvPermitAuthority(raw.lvPermitAuthority);
      raw.heOrShe = normalizeHeOrShe(raw.heOrShe);
      delete raw.appointed_location;
      delete raw.delete_as_appropriate;
      const res = await fetch(getBaseUrl() + "/api/sf-009-a-certificate-electrical/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          name: editForm.name || raw.certification?.name,
          date: editForm.date || raw.date,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-a-certificate-electrical/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setForms(forms.filter((f) => f._id !== deleteId));
        toast.success("Form deleted!");
      } else toast.error(data.message || "Failed to delete");
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const x = d instanceof Date ? d : parseDateForPicker(d);
    if (!x || isNaN(x.getTime())) {
      const s = String(d).trim();
      return s || "—";
    }
    return x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const safeDate = (value) => parseDateForPicker(value);

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const renderSignature = (sigVal) => (
    <div style={{ minHeight: 70, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }}>
      {sigVal ? (
        <img
          src={sigVal.startsWith("data:image") ? sigVal : `data:image/png;base64,${sigVal}`}
          alt="Signature"
          style={{ maxWidth: "100%", maxHeight: 64, objectFit: "contain" }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
      )}
    </div>
  );

  const fd = editForm?.formData || {};

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const raw = mergeRecordIntoDefaults(getRawFormData(record));
    const cert = raw.certification || {};
    const senior = raw.seniorAuthorisation || {};
    const appointedRole = normalizeAppointedRole(raw.appointedRole);
    const lvPermitAuthority = normalizeLvPermitAuthority(raw.lvPermitAuthority);
    const heOrShe = normalizeHeOrShe(raw.heOrShe);

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const tableMargin = { left: contentX, right: contentX };
    const pendingSignaturePromises = [];

    const addSignatureToPDF = (signatureData, x, y, maxWidth, maxHeight, pageNumber) =>
      new Promise((resolve) => {
        if (!signatureData) {
          resolve();
          return;
        }
        let sigSrc = signatureData;
        if (!String(sigSrc).startsWith("data:image")) sigSrc = `data:image/png;base64,${sigSrc}`;
        const sigImg = new Image();
        sigImg.crossOrigin = "anonymous";
        sigImg.onload = () => {
          try {
            const aspectRatio = sigImg.width / sigImg.height;
            let imgWidth = maxWidth;
            let imgHeight = maxWidth / aspectRatio;
            if (imgHeight > maxHeight) {
              imgHeight = maxHeight;
              imgWidth = maxHeight * aspectRatio;
            }
            doc.setPage(pageNumber);
            doc.addImage(sigSrc, "PNG", x, y, imgWidth, imgHeight, undefined, "NONE");
          } catch {}
          resolve();
        };
        sigImg.onerror = () => resolve();
        sigImg.src = sigSrc;
      });

    const safePdfDate = (v) => {
      if (!v) return "";
      const d = parseDateForPicker(v);
      if (!d) return String(v);
      return d.toLocaleDateString("en-GB");
    };

    let yPosition = await addLogoAndFormHeader(doc, "Certificate of Authorisation for Electrical Systems");

    const pdfGrid = {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
    };
    const pdfLbl = { fillColor: THEME_LIGHT_RGB, fontStyle: "bold" };
    const pdfVal = { fillColor: PDF_WHITE };
    const pdfHdr = { fillColor: THEME_LIGHT_RGB, fontStyle: "bold", fontSize: 8 };
    const pdfTxt = { fillColor: PDF_WHITE, valign: "top" };

    autoTable(doc, {
      startY: yPosition,
      body: [["Site:", raw.site || "", "Project No.", raw.projectNo || "", "Date:", safePdfDate(raw.date)]],
      columnStyles: {
        0: { cellWidth: contentW * 0.1, ...pdfLbl },
        1: { cellWidth: contentW * 0.23, ...pdfVal },
        2: { cellWidth: contentW * 0.12, ...pdfLbl },
        3: { cellWidth: contentW * 0.23, ...pdfVal },
        4: { cellWidth: contentW * 0.08, ...pdfLbl },
        5: { cellWidth: contentW * 0.24, ...pdfVal },
      },
      styles: pdfGrid,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [["I hereby certify that:"]],
      styles: { ...pdfGrid, ...pdfHdr },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
    });
    yPosition = doc.lastAutoTable.finalY;

    const certSig = cert.signature && typeof cert.signature === "string" ? (cert.signature.startsWith("data:") ? cert.signature : `data:image/png;base64,${cert.signature}`) : null;
    autoTable(doc, {
      startY: yPosition,
      body: [
        ["Name", cert.name || ""],
        ["Signature", { content: " ", styles: { minCellHeight: 22, fillColor: PDF_WHITE } }],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.18, ...pdfLbl },
        1: { cellWidth: contentW * 0.82, ...pdfVal },
      },
      styles: pdfGrid,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      didDrawCell: (data) => {
        if (data.section === "body" && data.row.index === 1 && data.column.index === 1 && certSig) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(certSig, data.cell.x + 3, data.cell.y + 3, 42, 14, pageNumber));
        }
      },
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [["Is appointed by the company as a:"]],
      styles: { ...pdfGrid, ...pdfHdr, fontSize: 7.5 },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [["Senior Authorised Person", " ", "Authorised Person", " "]],
      columnStyles: {
        0: { cellWidth: contentW * 0.42, ...pdfLbl },
        1: { cellWidth: contentW * 0.08, halign: "center", minCellHeight: 12, ...pdfVal },
        2: { cellWidth: contentW * 0.42, ...pdfLbl },
        3: { cellWidth: contentW * 0.08, halign: "center", minCellHeight: 12, ...pdfVal },
      },
      styles: pdfGrid,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      didDrawCell: (data) => {
        if (data.row.index !== 0) return;
        if (data.column.index === 1) drawPdfTickInCell(doc, data, appointedRole === "Senior Authorised Person");
        if (data.column.index === 3) drawPdfTickInCell(doc, data, appointedRole === "Authorised Person");
      },
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [
        [
          "This authorisation",
          " ",
          "includes",
          " ",
          "does not include authority to issue permits on LV Systems allowing work to be done on the company's behalf",
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.16, ...pdfTxt, fontStyle: "bold" },
        1: { cellWidth: contentW * 0.06, halign: "center", minCellHeight: 12, ...pdfVal },
        2: { cellWidth: contentW * 0.12, ...pdfTxt },
        3: { cellWidth: contentW * 0.06, halign: "center", minCellHeight: 12, ...pdfVal },
        4: { cellWidth: contentW * 0.6, ...pdfTxt },
      },
      styles: { ...pdfGrid, fontSize: 8, valign: "middle" },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      didDrawCell: (data) => {
        if (data.row.index !== 0) return;
        if (data.column.index === 1) drawPdfTickInCell(doc, data, lvPermitAuthority === "includes");
        if (data.column.index === 3) drawPdfTickInCell(doc, data, lvPermitAuthority === "does not include");
      },
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [
        [
          "He",
          " ",
          "she",
          " ",
          "has been issued with a copy of SF-009 LV Electrical Safety Rules and will ensure that staff under their control comply with these rules or with the agreement of the Company's Senior Authorised Person, or the customer's equivalent.",
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.06, ...pdfTxt, fontStyle: "bold" },
        1: { cellWidth: contentW * 0.06, halign: "center", minCellHeight: 12, ...pdfVal },
        2: { cellWidth: contentW * 0.08, ...pdfTxt },
        3: { cellWidth: contentW * 0.06, halign: "center", minCellHeight: 12, ...pdfVal },
        4: { cellWidth: contentW * 0.74, ...pdfTxt },
      },
      styles: { ...pdfGrid, fontSize: 8, valign: "middle" },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      didDrawCell: (data) => {
        if (data.row.index !== 0) return;
        if (data.column.index === 1) drawPdfTickInCell(doc, data, heOrShe === "He");
        if (data.column.index === 3) drawPdfTickInCell(doc, data, heOrShe === "She");
      },
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [["This authorisation is valid for a period of twelve months from the date of issue."]],
      styles: { ...pdfGrid, ...pdfTxt, fontStyle: "bold", fontSize: 8 },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
    });
    yPosition = doc.lastAutoTable.finalY;

    const seniorSig =
      senior.signature && typeof senior.signature === "string"
        ? senior.signature.startsWith("data:")
          ? senior.signature
          : `data:image/png;base64,${senior.signature}`
        : null;

    autoTable(doc, {
      startY: yPosition,
      body: [
        [
          "Authorised by",
          senior.authorisedBy || "",
          "Signature",
          { content: " ", styles: { minCellHeight: 22, fillColor: PDF_WHITE } },
          "Date",
          safePdfDate(senior.date),
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.14, ...pdfLbl },
        1: { cellWidth: contentW * 0.2, ...pdfVal },
        2: { cellWidth: contentW * 0.12, ...pdfLbl },
        3: { cellWidth: contentW * 0.22, ...pdfVal },
        4: { cellWidth: contentW * 0.08, ...pdfLbl },
        5: { cellWidth: contentW * 0.24, ...pdfVal },
      },
      styles: pdfGrid,
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      didDrawCell: (data) => {
        if (data.section === "body" && data.column.index === 3 && seniorSig) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(seniorSig, data.cell.x + 3, data.cell.y + 3, 38, 14, pageNumber));
        }
      },
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [
        [
          "Being the Company's Senior Authorised Person empowered by the Directors of the company to issue authorisation on behalf of the company.",
        ],
      ],
      styles: { ...pdfGrid, ...pdfTxt, fontSize: 7.5 },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
    });
    yPosition = doc.lastAutoTable.finalY;

    await Promise.all(pendingSignaturePromises);
    applyFooterAllPages(doc);

    const safeName = String(cert.name || "Certificate").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(formatDate(record.date || raw.date) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("A", getSf009SharePointFields("A", raw, record, getUserName))
        : `SF009A_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.site || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-A Certificate PDF generated successfully!");
    }
  };

  const renderCertificateForm = () => (
    <div style={{ border: sf009TblBorder, background: "#fff" }}>
      <div
        style={{
          textAlign: "center",
          padding: "14px 12px",
          fontWeight: 700,
          fontSize: 15,
          color: "#1a202c",
          borderBottom: sf009TblBorder,
          background: "#fff",
        }}
      >
        Certificate of Authorisation for Electrical Systems
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <tbody>
          <tr>
            <td style={{ ...sf009LblCell, width: "10%" }}>Site:</td>
            <td style={{ ...sf009ValCell, width: "23%" }}>
              <input type="text" value={fd.site || ""} onChange={(e) => updateFormData("_flat", "site", e.target.value)} style={sf009CellInput} />
            </td>
            <td style={{ ...sf009LblCell, width: "12%" }}>Project No.</td>
            <td style={{ ...sf009ValCell, width: "23%" }}>
              <input type="text" value={fd.projectNo || ""} onChange={(e) => updateFormData("_flat", "projectNo", e.target.value)} style={sf009CellInput} />
            </td>
            <td style={{ ...sf009LblCell, width: "8%" }}>Date:</td>
            <td style={{ ...sf009ValCell, width: "24%" }}>
              <DatePicker
                selected={safeDate(fd.date)}
                onChange={(d) => updateFormData("_flat", "date", d ? dateToLocalYMD(d) : "")}
                dateFormat="dd-MM-yyyy"
                className="form-control form-control-sm border-0 p-0"
                placeholderText="dd-MM-yyyy"
                wrapperClassName="w-100"
              />
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={sf009HdrCell}>
              I hereby certify that:
            </td>
          </tr>
          <tr>
            <td style={{ ...sf009LblCell, width: "18%" }}>Name</td>
            <td colSpan={5} style={sf009ValCell}>
              <input type="text" value={fd.certification?.name || ""} onChange={(e) => updateFormData("certification", "name", e.target.value)} style={sf009CellInput} />
            </td>
          </tr>
          <tr>
            <td style={sf009LblCell}>Signature</td>
            <td colSpan={5} style={sf009ValCell}>
              {renderSignature(fd.certification?.signature)}
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={sf009HdrCell}>
              Is appointed by the company as a:
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009ValCell, padding: "10px 12px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
                {APPOINTED_ROLE_OPTIONS.map((opt) => (
                  <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="appointedRole"
                      className="form-check-input theme-checkbox"
                      checked={normalizeAppointedRole(fd.appointedRole) === opt}
                      onChange={() => updateFormData("_flat", "appointedRole", opt)}
                      style={{ width: 16, height: 16, margin: 0, accentColor: THEME, cursor: "pointer" }}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={sf009TxtCell}>
              This authorisation includes / does not include authority to issue permits on LV Systems allowing work to be done on the company's behalf
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009ValCell, padding: "10px 12px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
                {LV_PERMIT_AUTHORITY_OPTIONS.map((opt) => (
                  <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="lvPermitAuthority"
                      className="form-check-input theme-checkbox"
                      checked={normalizeLvPermitAuthority(fd.lvPermitAuthority) === opt}
                      onChange={() => updateFormData("_flat", "lvPermitAuthority", opt)}
                      style={{ width: 16, height: 16, margin: 0, accentColor: THEME, cursor: "pointer" }}
                    />
                    <span style={{ textTransform: "capitalize" }}>{opt}</span>
                  </label>
                ))}
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009ValCell, padding: "10px 12px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, lineHeight: 1.55 }}>
                {HE_OR_SHE_OPTIONS.map((opt) => (
                  <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="heOrShe"
                      className="form-check-input theme-checkbox"
                      checked={normalizeHeOrShe(fd.heOrShe) === opt}
                      onChange={() => updateFormData("_flat", "heOrShe", opt)}
                      style={{ width: 16, height: 16, margin: 0, accentColor: THEME, cursor: "pointer" }}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
                <span>
                  has been issued with a copy of SF-009 LV Electrical Safety Rules and will ensure that staff under their control comply with these rules or with the agreement of the Company's Senior Authorised Person, or the customer's equivalent.
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, fontWeight: 700 }}>
              This authorisation is valid for a period of twelve months from the date of issue.
            </td>
          </tr>
          <tr>
            <td style={{ ...sf009LblCell, width: "16%" }}>Authorised by</td>
            <td style={{ ...sf009ValCell, width: "22%" }}>
              <input
                type="text"
                value={fd.seniorAuthorisation?.authorisedBy || ""}
                onChange={(e) => updateFormData("seniorAuthorisation", "authorisedBy", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td style={{ ...sf009LblCell, width: "12%" }}>Signature</td>
            <td style={{ ...sf009ValCell, width: "22%" }}>{renderSignature(fd.seniorAuthorisation?.signature)}</td>
            <td style={{ ...sf009LblCell, width: "8%" }}>Date</td>
            <td style={{ ...sf009ValCell, width: "20%" }}>
              <DatePicker
                selected={safeDate(fd.seniorAuthorisation?.date)}
                onChange={(d) => updateFormData("seniorAuthorisation", "date", d ? dateToLocalYMD(d) : "")}
                dateFormat="dd-MM-yyyy"
                className="form-control form-control-sm border-0 p-0"
                placeholderText="dd-MM-yyyy"
                wrapperClassName="w-100"
              />
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, fontSize: 11 }}>
              Being the Company's Senior Authorised Person empowered by the Directors of the company to issue authorisation on behalf of the company.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-A Certificate of Authorisation for Electrical Systems</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project No.</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const cert = raw.certification || {};
                  const updatedDate = r.updatedAt || r.createdAt || r.date || raw.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.site || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.projectNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{cert.name || r.name || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.date || raw.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.site || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(26, 32, 44, 0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 920, margin: "0 auto", border: "none", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(139, 22, 52, 0.15)" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>
                  Edit SF-009-A Certificate of Authorisation for Electrical Systems
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "20px 24px 8px", background: "#fff" }}>
                {renderCertificateForm()}
              </div>
              <div className="modal-footer" style={{ background: "#fff", borderTop: `1px solid ${THEME_BORDER}`, padding: "14px 24px", gap: 10 }}>
                <button type="button" className="btn btn-light" style={{ borderRadius: 8, fontWeight: 600, border: `1px solid ${THEME_BORDER}` }} onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 22px", border: "none" }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-A Certificate of Authorisation for Electrical Systems File"
        importPath="sf-009-a-certificate-electrical/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-A Certificate</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009ACertificateElectricalPage;
